import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Static fallback rates (USD = 1). Replaced at runtime by live rates from open.er-api.com.
const STATIC = {
    USD: { code: "USD", symbol: "$", rate: 1, locale: "en-US" },
    EUR: { code: "EUR", symbol: "€", rate: 0.92, locale: "de-DE" },
    COP: { code: "COP", symbol: "$", rate: 4150, locale: "es-CO" },
};

const RATES_CACHE_KEY = "wcr_fx_cache_v1";
const RATES_TTL_MS = 60 * 60 * 1000; // 1 hour

async function fetchLiveRates() {
    // Try cache first.
    // NOTE: localStorage here stores only a non-sensitive FX-rate cache (public
    // exchange rates) and the user's currency preference — never tokens/PII.
    // Auth uses httpOnly cookies, so XSS cannot read anything sensitive from here.
    try {
        const cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || "null");
        if (cached && cached.ts && (Date.now() - cached.ts) < RATES_TTL_MS && cached.rates) {
            return { rates: cached.rates, source: "cache" };
        }
    } catch (e) { console.debug("currency rate cache read failed", e); }

    try {
        const res = await fetch("https://open.er-api.com/v6/latest/USD", { cache: "no-store" });
        if (!res.ok) throw new Error("rate fetch failed");
        const data = await res.json();
        if (data?.result !== "success" || !data?.rates) throw new Error("bad response");
        const picked = { USD: 1, EUR: data.rates.EUR, COP: data.rates.COP };
        try {
            localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ ts: Date.now(), rates: picked }));
        } catch (e) { console.debug("currency rate cache write skipped", e); }
        return { rates: picked, source: "live" };
    } catch {
        return { rates: null, source: "fallback" };
    }
}

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
    const [code, setCodeState] = useState(() => {
        try { return localStorage.getItem("wcr_currency") || "USD"; } catch { return "USD"; }
    });
    const [currencies, setCurrencies] = useState(STATIC);
    const [source, setSource] = useState("fallback");

    const setCode = useCallback((c) => {
        if (!STATIC[c]) return;
        setCodeState(c);
        try { localStorage.setItem("wcr_currency", c); } catch (e) { console.debug("currency preference not persisted", e); }
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const { rates, source: src } = await fetchLiveRates();
            if (!mounted) return;
            if (rates) {
                setCurrencies({
                    USD: { ...STATIC.USD, rate: rates.USD ?? 1 },
                    EUR: { ...STATIC.EUR, rate: rates.EUR ?? STATIC.EUR.rate },
                    COP: { ...STATIC.COP, rate: rates.COP ?? STATIC.COP.rate },
                });
                setSource(src);
            } else {
                setSource("fallback");
            }
        })();
        return () => { mounted = false; };
    }, []);

    const currency = currencies[code] || currencies.USD;

    const convert = useCallback((amountUsd) => Number(amountUsd || 0) * currency.rate, [currency]);

    const format = useCallback((amountUsd, opts = {}) => {
        const value = convert(amountUsd);
        const maxFrac = currency.code === "COP" ? 0 : (opts.maximumFractionDigits ?? 0);
        try {
            return new Intl.NumberFormat(currency.locale, {
                style: "currency",
                currency: currency.code,
                maximumFractionDigits: maxFrac,
                minimumFractionDigits: 0,
            }).format(value);
        } catch {
            return `${currency.symbol}${Math.round(value).toLocaleString()}`;
        }
    }, [currency, convert]);

    const value = useMemo(
        () => ({ code, setCode, currency, format, convert, all: currencies, source }),
        [code, setCode, currency, format, convert, currencies, source]
    );

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const ctx = useContext(CurrencyContext);
    if (!ctx) throw new Error("useCurrency outside CurrencyProvider");
    return ctx;
}
